import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Checkbox,
    Collapse,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    Tooltip,
    useColorMode,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import {
    AiOutlineFileText,
    AiOutlineDollarCircle,
    AiOutlineSchedule,
    AiOutlineCalendar,
    AiOutlineCloseCircle,
    AiOutlineDelete,
    AiOutlineDown,
    AiOutlineEdit,
    AiOutlineEye,
    AiOutlineFlag,
    AiOutlineHistory,
    AiOutlineMessage,
    AiOutlineWarning,
    AiOutlineClockCircle,
    AiOutlineCheckCircle,
    AiOutlinePhone,
    AiOutlineMail,
    AiOutlineTeam,
    AiOutlineWhatsApp,
    AiOutlineEnvironment,
    AiOutlineCheckSquare,
    AiOutlineFilter,
    AiOutlineSearch,
    AiOutlineUser,
    AiOutlineShop,
} from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { useComercial } from '../../../../contexts/ComercialContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import api from '../../../../services/api';
import ConfirmDeleteDialog from '../../../../components/UI/ConfirmDeleteDialog';
import TablePagination from '../../../../components/UI/TablePagination';
import { AnexosEmBreve } from '../../../../components/UI/AnotacaoFormSection';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Janela do estado "perto de vencer" (dias a partir de hoje)
const DIAS_PROXIMO_VENCIMENTO = 7;

// Data local (não UTC): evita virar o "hoje" às 21h no fuso do Brasil
const hojeISO = () => new Date().toLocaleDateString('en-CA');

const diasAtraso = (dataISO) => {
    if (!dataISO) return 0;
    const diff = new Date(`${hojeISO()}T00:00:00`) - new Date(`${String(dataISO).slice(0, 10)}T00:00:00`);
    return Math.max(0, Math.floor(diff / 86400000));
};

const diasAte = (dataISO) => {
    if (!dataISO) return null;
    const diff = new Date(`${String(dataISO).slice(0, 10)}T00:00:00`) - new Date(`${hojeISO()}T00:00:00`);
    return Math.round(diff / 86400000);
};

// Estado de prazo: atrasado / proximo (vence em até N dias) / emDia
const statusDePrazo = (dataISO) => {
    if (!dataISO) return 'emDia';
    const d = String(dataISO).slice(0, 10);
    if (d < hojeISO()) return 'atrasado';
    const dias = diasAte(d);
    return dias <= DIAS_PROXIMO_VENCIMENTO ? 'proximo' : 'emDia';
};

const ORDEM_STATUS = { atrasado: 0, proximo: 1, emDia: 2, historico: 3 };

const STATUS_CORES = {
    atrasado: { cor: '#FC8181', bg: 'rgba(252,129,129,0.12)' },
    proximo:  { cor: '#ECC94B', bg: 'rgba(236,201,75,0.14)' },
    emDia:    { cor: 'secondary', bg: 'secondarySubtle' },
    historico:{ cor: 'textMuted', bg: 'surfaceHover' },
};

const STATUS_ICONS = {
    atrasado: AiOutlineWarning,
    proximo: AiOutlineClockCircle,
    emDia: AiOutlineCheckCircle,
    historico: AiOutlineHistory,
};

const SituacaoBadge = ({ status, label, bg, color }) => {
    const StatusIcon = STATUS_ICONS[status] || AiOutlineHistory;
    return (
        <Badge bg={bg} color={color} fontSize="10px" fontWeight="800" borderRadius="md" px={2} py={1}
            display="inline-flex" alignItems="center" gap={1} whiteSpace="nowrap">
            <Icon as={StatusIcon} boxSize="10px" />
            <Text>{label}</Text>
        </Badge>
    );
};
const atividadePendente = (atividade) => {
    const status = String(atividade?.status || '').toLowerCase();
    return !atividade?.completed && !atividade?.canceled && !status.includes('cancel') && !status.includes('conclu');
};

const cardAtivo = (card) => !card?.completed && !card?.canceled;
const temObservacaoLegada = (item) => Boolean(String(item?.observacao || item?.observacoes || '').trim());


const statusDaAtividade = (atividade) => (
    atividadePendente(atividade)
        ? statusDePrazo(atividade.dataFim || atividade.dataInicio)
        : 'historico'
);


const fmtData = (d) => (!d ? '—' : new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR'));
const fmtMoney = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Badge de situação (label + cores) — usado na tabela e na ficha de acompanhamento
const situacaoDeCard = (statusProprio, historico, dataFechamento) => {
    if (historico) return { label: 'Histórico', bg: 'surfaceHover', cor: 'textMuted' };
    if (statusProprio === 'atrasado') return { label: `Atrasado há ${diasAtraso(dataFechamento)} dia(s)`, bg: 'rgba(252,129,129,0.12)', cor: '#FC8181' };
    if (statusProprio === 'proximo') {
        const restam = diasAte(dataFechamento);
        return { label: restam === 0 ? 'Fecha hoje' : `Fecha em ${restam} dia(s)`, bg: 'rgba(236,201,75,0.14)', cor: '#B7791F' };
    }
    return { label: 'Em dia', bg: 'secondarySubtle', cor: 'secondary' };
};

const situacaoDeAtividade = (a) => {
    const st = statusDaAtividade(a);
    if (st === 'historico') return { label: 'Histórico', bg: 'surfaceHover', cor: 'textMuted' };
    if (st === 'atrasado') return { label: `Atrasada há ${diasAtraso(a.dataFim || a.dataInicio)} dia(s)`, bg: 'rgba(252,129,129,0.12)', cor: '#FC8181' };
    if (st === 'proximo') {
        const restam = diasAte(a.dataFim || a.dataInicio);
        return { label: restam === 0 ? 'Vence hoje' : `Vence em ${restam} dia(s)`, bg: 'rgba(236,201,75,0.14)', cor: '#B7791F' };
    }
    return { label: 'Em dia', bg: 'secondarySubtle', cor: 'secondary' };
};

// Campo rotulado da ficha de acompanhamento
const CampoInfo = ({ label, children }) => (
    <Box bg="surfaceRaised" border="1px solid" borderColor="borderSubtle" borderRadius="lg" p={2.5}>
        <Text fontSize="9px" fontWeight="800" color="textMuted" textTransform="uppercase" letterSpacing="1px" mb={0.5}>{label}</Text>
        {children}
    </Box>
);

const timeAgo = (dateStr) => {
    const diffMs = new Date() - new Date(dateStr);
    const mins = Math.floor(diffMs / 60000);
    const horas = Math.floor(mins / 60);
    const dias = Math.floor(horas / 24);
    if (mins < 1) return 'agora';
    if (mins < 60) return `há ${mins} min`;
    if (horas < 24) return `há ${horas}h`;
    return `há ${dias} dia(s)`;
};

const TIPO_ICONES = {
    'Ligação': AiOutlinePhone,
    'E-mail': AiOutlineMail,
    'Reunião': AiOutlineTeam,
    'WhatsApp': AiOutlineWhatsApp,
    'Visita': AiOutlineEnvironment,
    'Tarefa': AiOutlineCheckSquare,
};

// ─── Linha do tempo: eventos derivados dos dados existentes (camada 1) ───────
// Nenhuma mudança no banco: os eventos de "sistema" são reconstruídos na hora
// a partir do negócio e das atividades já carregados no contexto.

const dataHoraDe = (d, h) => {
    if (!d) return null;
    const hora = h && h !== '00:00' ? h : '12:00';
    return new Date(`${String(d).slice(0, 10)}T${hora}:00`);
};

const horaLabel = (h) => (h && h !== '00:00' ? ` às ${h}` : '');

// Estilos por tipo de entrada da linha do tempo
const TL_ESTILOS = {
    nota:       { icon: AiOutlineMessage,     cor: 'secondary', bg: 'secondarySubtle' },
    alteracao:  { icon: AiOutlineEdit,        cor: '#9F7AEA',   bg: 'rgba(159,122,234,0.15)' },
    remarcacao: { icon: AiOutlineCalendar,    cor: '#4299E1',   bg: 'rgba(66,153,225,0.15)' },
    agendada:   { icon: AiOutlineCalendar,    cor: '#718096',   bg: 'surfaceHover' },
    concluida:  { icon: AiOutlineCheckCircle, cor: 'secondary', bg: 'secondarySubtle' },
    cancelada:  { icon: AiOutlineCloseCircle, cor: '#718096',   bg: 'surfaceHover' },
    vencida:    { icon: AiOutlineWarning,     cor: '#FC8181',   bg: 'rgba(252,129,129,0.12)' },
    inicio:     { icon: AiOutlineFlag,        cor: '#718096',   bg: 'surfaceHover' },
};

const eventosDeAtividade = (a, comNome) => {
    const hoje = hojeISO();
    const eventos = [];
    const rotulo = comNome ? `Atividade "${a.nome}"${a.tipo ? ` (${a.tipo})` : ''}` : 'Atividade';
    const inicio = dataHoraDe(a.dataInicio, a.horaInicio);
    const fim = dataHoraDe(a.dataFim || a.dataInicio, a.horaFim || a.horaInicio);
    const concluida = a.completed || String(a.status || '').toLowerCase().includes('conclu');
    const cancelada = String(a.status || '').toLowerCase().includes('cancel');
    const dataRef = a.dataFim || a.dataInicio;
    const observacaoAnterior = String(a.observacao || '').trim();

    if (observacaoAnterior) {
        eventos.push({
            key: `atv-${a.id}-observacao-anterior`, kind: 'nota', estilo: 'nota',
            texto: `Observa\u00e7\u00e3o anterior: ${comNome ? `${rotulo} \u00b7 ` : ''}${observacaoAnterior}`,
            quando: null,
            meta: 'registro anterior \u00b7 data original indispon\u00edvel',
        });
    }

    if (inicio) {
        eventos.push({
            key: `atv-${a.id}-agendada`, kind: 'sistema', estilo: 'agendada',
            texto: `${rotulo} agendada para ${fmtData(a.dataInicio)}${horaLabel(a.horaInicio)}.`,
            quando: inicio,
        });
    }
    if (concluida) {
        eventos.push({
            key: `atv-${a.id}-concluida`, kind: 'sistema', estilo: 'concluida',
            texto: `${rotulo} concluída.`, quando: fim,
        });
    } else if (cancelada) {
        eventos.push({
            key: `atv-${a.id}-cancelada`, kind: 'sistema', estilo: 'cancelada',
            texto: `${rotulo} cancelada.`, quando: fim,
        });
    } else if (dataRef && dataRef < hoje) {
        eventos.push({
            key: `atv-${a.id}-vencida`, kind: 'sistema', estilo: 'vencida',
            texto: `${rotulo} venceu sem ser concluída.`, quando: fim,
        });
    }
    return eventos;
};

const eventosDeCard = (card, cardAtrasado, atividadesDoCard) => {
    const eventos = [];
    const observacaoAnterior = String(card.observacoes || '').trim();
    if (observacaoAnterior) {
        eventos.push({
            key: `card-${card.id}-observacao-anterior`, kind: 'nota', estilo: 'nota',
            texto: `Observa\u00e7\u00e3o anterior: ${observacaoAnterior}`,
            quando: null,
            meta: 'registro anterior \u00b7 data original indispon\u00edvel',
        });
    }
    if (card.dataInicio) {
        eventos.push({
            key: `card-${card.id}-inicio`, kind: 'sistema', estilo: 'inicio',
            texto: `Negócio iniciado — ${fmtMoney(card.valor)}${card.dataFechamento ? ` · fechamento previsto para ${fmtData(card.dataFechamento)}` : ''}.`,
            quando: dataHoraDe(card.dataInicio),
        });
    }
    if (cardAtrasado && card.dataFechamento) {
        eventos.push({
            key: `card-${card.id}-fechamento-vencido`, kind: 'sistema', estilo: 'vencida',
            texto: `Data de fechamento venceu (${fmtData(card.dataFechamento)}).`,
            quando: dataHoraDe(card.dataFechamento),
        });
    }
    (atividadesDoCard || []).forEach(a => eventos.push(...eventosDeAtividade(a, true)));
    return eventos;
};

// ─── Linha do tempo de um item (nova observação + trilha de acontecimentos) ──
// Fora da página para manter a identidade do componente entre re-renders
// (não perde o rascunho de observação ao expandir/minimizar outros cards).

const TL_CHIPS = [
    { id: 'tudo',       label: 'Tudo' },
    { id: 'nota',       label: 'Observações' },
    { id: 'alteracao',  label: 'Alterações' },
    { id: 'remarcacao', label: 'Remarcações' },
    { id: 'sistema',    label: 'Sistema' },
];

// Identidade visual por tipo de linha do tempo: verde = negócio, azul = atividade.
// A borda esquerda colorida e o chip do ícone dizem de quem é a caixa sem depender do texto.
const TL_ACCENTS = {
    negocio:   { cor: 'secondary', bg: 'secondarySubtle', icon: AiOutlineHistory },
    atividade: { cor: '#4299E1', bg: 'rgba(66,153,225,0.15)', icon: AiOutlineSchedule },
};

const TimelineSection = ({ refType, refId, notas, eventos, onChanged, titulo = 'Linha do tempo', subtitulo, accent = 'negocio', mostrarAnexos = true }) => {
    const toast = useToast();
    const [texto, setTexto] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [excluindo, setExcluindo] = useState(null);
    const [filtroTl, setFiltroTl] = useState('tudo');
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

    const itens = useMemo(() => {
        const KIND_LABELS = { nota: 'observação', remarcacao: 'remarcação', alteracao: 'alteração' };
        const doBanco = (notas || []).map(n => {
            // kind vem do banco (auditoria do backend); fallback por regex para
            // linhas antigas criadas antes da coluna kind existir
            const kind = ['nota', 'remarcacao', 'alteracao'].includes(n.kind)
                ? n.kind
                : (/^(atividade remarcada|negócio remarcado)/i.test(String(n.text)) ? 'remarcacao' : 'nota');
            return {
                key: `nota-${n.id}`,
                kind,
                estilo: kind,
                texto: n.text,
                quando: n.created_at ? new Date(n.created_at) : null,
                meta: `${n.author || 'Sistema'} · ${KIND_LABELS[kind]} · ${timeAgo(n.created_at)}`,
                // Só observações manuais podem ser excluídas — remarcações e
                // alterações são trilha de auditoria e ficam protegidas
                nota: kind === 'nota' ? n : null,
            };
        });
        const doSistema = (eventos || []).map(e => ({
            ...e,
            meta: e.meta || `sistema · ${e.quando ? e.quando.toLocaleDateString('pt-BR') : '—'}`,
        }));
        return [...doBanco, ...doSistema].sort((a, b) => (b.quando?.getTime() || 0) - (a.quando?.getTime() || 0));
    }, [notas, eventos]);

    const visiveis = filtroTl === 'tudo' ? itens : itens.filter(i => i.kind === filtroTl);

    const salvar = async () => {
        if (!texto.trim()) return;
        setSalvando(true);
        try {
            await api.post('/comercial/anotacoes', { refType, refId: Number(refId), text: texto.trim() });
            setTexto('');
            await onChanged();
        } catch (err) {
            toast({ title: 'Erro ao salvar anotação', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
        } finally {
            setSalvando(false);
        }
    };

    const confirmarExclusao = async () => {
        try {
            await api.delete(`/comercial/anotacoes/${excluindo.id}`);
            onDelClose();
            setExcluindo(null);
            await onChanged();
        } catch (err) {
            toast({ title: 'Erro ao excluir anotação', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
        }
    };

    const acc = TL_ACCENTS[accent] || TL_ACCENTS.negocio;

    return (
        <Box mt={2} bg="surfaceRaised" border="1px solid" borderColor="borderSubtle"
            borderLeft="3px solid" borderLeftColor={acc.cor} borderRadius="lg" p={3}>
            <Flex align="center" gap={2} mb={3}>
                <Flex w="26px" h="26px" borderRadius="md" bg={acc.bg} align="center" justify="center" flexShrink={0}>
                    <Icon as={acc.icon} color={acc.cor} boxSize="14px" />
                </Flex>
                <Box minW={0}>
                    <Text fontSize="xs" fontWeight="800" color="textPrimary" textTransform="uppercase" letterSpacing="1px">
                        {titulo}
                    </Text>
                    {subtitulo && (
                        <Text fontSize="10px" color="textMuted" noOfLines={1}>{subtitulo}</Text>
                    )}
                </Box>
            </Flex>

            <Flex gap={2} align="flex-end" mb={3}>
                <Textarea
                    value={texto}
                    onChange={e => setTexto(e.target.value)}
                    placeholder="Nova observação (ex: kick-off não realizado — cliente faltou na reunião)..."
                    size="sm"
                    rows={2}
                    borderRadius="md"
                    onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) salvar(); }}
                />
                <Button size="sm" variant="primary" flexShrink={0} onClick={salvar} isLoading={salvando} isDisabled={!texto.trim()}>
                    Anotar
                </Button>
            </Flex>

            {mostrarAnexos && (
                <Box mb={3}>
                    <AnexosEmBreve />
                </Box>
            )}

            <HStack spacing={1.5} mb={3} flexWrap="wrap">
                {TL_CHIPS.map(c => {
                    const active = filtroTl === c.id;
                    return (
                        <Button key={c.id} size="xs" variant="ghost"
                            border="1px solid" borderColor={active ? 'rgba(1,233,117,0.45)' : 'borderSubtle'}
                            bg={active ? 'secondarySubtle' : 'transparent'} color={active ? 'secondary' : 'textSecondary'}
                            _hover={{ bg: active ? 'secondarySubtle' : 'surfaceHover', color: active ? 'secondary' : 'textPrimary' }}
                            borderRadius="full" fontWeight="700" onClick={() => setFiltroTl(c.id)}>
                            {c.label}
                        </Button>
                    );
                })}
            </HStack>

            {visiveis.length === 0 ? (
                <Text fontSize="xs" color="textMuted">
                    {itens.length === 0
                        ? 'Nenhum acontecimento ainda. Registre o ocorrido acima.'
                        : 'Nada neste filtro.'}
                </Text>
            ) : (
                <Box position="relative" pl="26px">
                    <Box position="absolute" left="9px" top="8px" bottom="8px" w="2px" bg="borderSubtle" />
                    <VStack align="stretch" spacing={3}>
                        {visiveis.map(item => {
                            const estilo = TL_ESTILOS[item.estilo] || TL_ESTILOS.nota;
                            return (
                                <Box key={item.key} position="relative">
                                    <Flex position="absolute" left="-26px" top="0" w="20px" h="20px" borderRadius="full"
                                        bg={estilo.bg} border="1px solid" borderColor="borderSubtle" align="center" justify="center">
                                        <Icon as={estilo.icon} color={estilo.cor} boxSize="11px" />
                                    </Flex>
                                    <Flex align="start" gap={2}>
                                        <Box flex="1" minW={0}>
                                            <Text fontSize="xs" color="textPrimary" whiteSpace="pre-wrap">{item.texto}</Text>
                                            <Text fontSize="10px" color="textMuted" mt={0.5}>{item.meta}</Text>
                                        </Box>
                                        {item.nota && (
                                            <IconButton
                                                aria-label="Excluir anotação"
                                                icon={<AiOutlineDelete />}
                                                size="xs"
                                                variant="ghost"
                                                color="textMuted"
                                                _hover={{ color: 'danger', bg: 'transparent' }}
                                                onClick={() => { setExcluindo(item.nota); onDelOpen(); }}
                                            />
                                        )}
                                    </Flex>
                                </Box>
                            );
                        })}
                    </VStack>
                </Box>
            )}

            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={() => { onDelClose(); setExcluindo(null); }}
                onConfirm={confirmarExclusao}
                title="Excluir Anotação"
                itemName={excluindo ? `${String(excluindo.text).slice(0, 40)}${String(excluindo.text).length > 40 ? '…' : ''}` : ''}
            />
        </Box>
    );
};

// ─── Linha de atividade (filha da árvore ou solta) ───────────────────────────

const AtividadeRow = ({ a, status, historico, notas, notasAbertas, onToggleNotas, onRemarcar, onVer, onChanged }) => {
    const st = status || statusDaAtividade(a);
    const stCores = STATUS_CORES[st];
    const totalObservacoes = notas.length + (temObservacaoLegada(a) ? 1 : 0);
    const badgeLabel = situacaoDeAtividade(a).label;
    return (
        <Box>
            <Flex
                align="center" gap={3} px={3} py={2.5}
                bg="surface" border="1px solid" borderColor="borderSubtle" borderRadius="lg"
                _hover={{ borderColor: 'borderPrimary' }} transition="border-color 0.15s"
                wrap="wrap"
                cursor="pointer"
                onClick={onToggleNotas}
            >
                <Icon as={AiOutlineDown} boxSize="10px" color="textMuted" flexShrink={0}
                    transform={notasAbertas ? 'rotate(180deg)' : 'rotate(0deg)'} transition="transform 0.2s" />
                <Flex w="26px" h="26px" borderRadius="md" bg={stCores.bg} align="center" justify="center" flexShrink={0}>
                    <Icon as={TIPO_ICONES[a.tipo] || AiOutlineSchedule} color={stCores.cor} boxSize="14px" />
                </Flex>
                <Box flex="1" minW="180px">
                    <Text fontSize="sm" fontWeight="700" color="textPrimary" noOfLines={1}>{a.nome}</Text>
                    <HStack spacing={2} mt={0.5}>
                        <Text fontSize="10px" color="textMuted">{a.tipo || 'Atividade'}</Text>
                        {a.responsavel && (
                            <HStack spacing={1}>
                                <Icon as={AiOutlineUser} boxSize="10px" color="textMuted" />
                                <Text fontSize="10px" color="textMuted">{a.responsavel}</Text>
                            </HStack>
                        )}
                        <Text fontSize="10px" color="textMuted">{fmtData(a.dataFim || a.dataInicio)} {a.horaFim || a.horaInicio}</Text>
                    </HStack>
                </Box>
                <SituacaoBadge status={st} label={badgeLabel} bg={stCores.bg} color={stCores.cor} />
                <HStack spacing={1} onClick={e => e.stopPropagation()}>
                    <Tooltip label={`Linha do tempo · ${totalObservacoes} registro(s)`} fontSize="xs">
                        <Button size="xs" variant="ghost" color={totalObservacoes > 0 ? 'secondary' : 'textSecondary'}
                            leftIcon={<AiOutlineMessage />} onClick={onToggleNotas} fontWeight="700">
                            {totalObservacoes}
                        </Button>
                    </Tooltip>
                    {!historico && (
                        <Tooltip label="Remarcar atividade" fontSize="xs">
                            <IconButton aria-label="Remarcar" icon={<AiOutlineCalendar />} size="xs" variant="ghost"
                                color="textSecondary" _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                onClick={onRemarcar} />
                        </Tooltip>
                    )}
                    <Tooltip label="Abrir a ficha completa em Atividades" fontSize="xs">
                        <Button size="xs" variant="ghost" color="textSecondary" fontWeight="700"
                            leftIcon={<AiOutlineEye />} _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                            onClick={onVer}>
                            Ver card
                        </Button>
                    </Tooltip>
                </HStack>
            </Flex>
            <Collapse in={notasAbertas} animateOpacity>
                <Box pl={6}>
                    <TimelineSection refType="atividade" refId={a.id}
                        titulo="Linha do tempo da atividade" subtitulo={a.nome}
                        accent="atividade" mostrarAnexos={false}
                        notas={notas} eventos={eventosDeAtividade(a, false)} onChanged={onChanged} />
                </Box>
            </Collapse>
        </Box>
    );
};

// ─── Página ──────────────────────────────────────────────────────────────────

const GRID_COLUMNS = "180px 1fr 95px 140px 95px 105px 95px 90px 95px";

const Anotacoes = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { colorMode } = useColorMode();
    const { atividades, pipelineCards, updateAtividade, updatePipelineCard } = useComercial();
    const { employees } = usePermissions();

    const [anotacoes, setAnotacoes] = useState([]);
    const [filtro, setFiltro] = useState('todas');
    const [busca, setBusca] = useState('');
    const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');
    const [abertas, setAbertas] = useState({});       // chaves "atividade-id" com a linha do tempo aberta DENTRO da ficha
    const [detalhe, setDetalhe] = useState(null);     // { kind: 'card' | 'atividade', id } — ficha de acompanhamento aberta em modal
    const [remarcando, setRemarcando] = useState(null); // { tipo: 'atividade'|'card', item }
    const [formRemarcar, setFormRemarcar] = useState({ dataInicio: '', horaInicio: '', dataFim: '', horaFim: '', dataFechamento: '' });
    const [salvandoRemarcar, setSalvandoRemarcar] = useState(false);

    const fetchAnotacoes = useCallback(async () => {
        try {
            const { data } = await api.get('/comercial/anotacoes');
            setAnotacoes(data || []);
        } catch (err) {
            console.error('Erro ao carregar anotações:', err);
        }
    }, []);

    useEffect(() => { fetchAnotacoes(); }, [fetchAnotacoes]);

    // ── Acompanhamento: negócios em aberto + atividades pendentes ───────────
    const hoje = hojeISO();

    const referenciasAnotadas = useMemo(() => {
        const cards = new Set();
        const atividadesComNota = new Set();
        (anotacoes || []).forEach(nota => {
            const id = String(nota.ref_id);
            if (nota.ref_type === 'card') cards.add(id);
            if (nota.ref_type === 'atividade') atividadesComNota.add(id);
        });
        return { cards, atividades: atividadesComNota };
    }, [anotacoes]);

    const atividadesAcompanhadas = useMemo(() =>
        (atividades || []).filter(a =>
            atividadePendente(a) || referenciasAnotadas.atividades.has(String(a.id)) || temObservacaoLegada(a)
        ),
        [atividades, referenciasAnotadas]
    );

    // Árvore: nó de negócio em aberto → atividades pendentes vinculadas.
    // Status do nó = pior estado entre o fechamento do negócio e as filhas.
    const arvore = useMemo(() => {
        const porCard = {};
        atividadesAcompanhadas.forEach(a => {
            if (a.cardId != null) {
                const k = String(a.cardId);
                (porCard[k] = porCard[k] || []).push(a);
            }
        });

        const nodes = (pipelineCards || [])
            .filter(card => (
                cardAtivo(card) ||
                referenciasAnotadas.cards.has(String(card.id)) ||
                temObservacaoLegada(card) ||
                (porCard[String(card.id)] || []).length > 0
            ))
            .map(card => {
                const filhas = (porCard[String(card.id)] || [])
                    .sort((a, b) => (a.dataFim || a.dataInicio || '').localeCompare(b.dataFim || b.dataInicio || ''));
                const historico = !cardAtivo(card);
                const statusProprio = historico ? 'historico' : statusDePrazo(card.dataFechamento);
                const statusFilhas = filhas.map(statusDaAtividade);
                const status = historico
                    ? 'historico'
                    : [statusProprio, ...statusFilhas]
                        .sort((a, b) => ORDEM_STATUS[a] - ORDEM_STATUS[b])[0];
                return { card, status, statusProprio, filhas, historico };
            });

        nodes.sort((a, b) =>
            (ORDEM_STATUS[a.status] - ORDEM_STATUS[b.status]) ||
            String(a.card.dataFechamento || '9999-12-31').localeCompare(String(b.card.dataFechamento || '9999-12-31')));

        const cardsDisponiveis = new Set((pipelineCards || []).map(card => String(card.id)));
        const soltas = atividadesAcompanhadas
            .filter(a => a.cardId == null || !cardsDisponiveis.has(String(a.cardId)))
            .map(a => ({ a, status: statusDaAtividade(a) }))
            .sort((x, y) => ORDEM_STATUS[x.status] - ORDEM_STATUS[y.status]);

        return { nodes, soltas };
    }, [atividadesAcompanhadas, pipelineCards, referenciasAnotadas]);

    const contagens = useMemo(() => {
        const c = { todas: arvore.nodes.length + arvore.soltas.length, atrasado: 0, proximo: 0, emDia: 0, historico: 0 };
        arvore.nodes.forEach(n => {
            c[n.status] += 1;
            const temHistorico = n.statusProprio === 'historico' || n.filhas.some(f => statusDaAtividade(f) === 'historico');
            if (temHistorico && n.status !== 'historico') c.historico += 1;
        });
        arvore.soltas.forEach(s => {
            c[s.status] += 1;
        });
        return c;
    }, [arvore]);

    // Busca livre + filtro por responsável (mesmo padrão das telas Contatos/Atividades).
    // O nó do negócio permanece visível se ele OU alguma atividade filha corresponder.
    const nodesFiltrados = useMemo(() => {
        let lista = arvore.nodes;
        if (filtro === 'historico') {
            lista = lista
                .filter(n => n.statusProprio === 'historico' || n.filhas.some(f => statusDaAtividade(f) === 'historico'))
                .map(n => ({ ...n, filhas: n.filhas.filter(f => statusDaAtividade(f) === 'historico') }));
        } else if (filtro !== 'todas') {
            lista = lista.filter(n => n.status === filtro);
        }
        if (filterEmployeeIds.length > 0) {
            lista = lista.filter(n =>
                filterEmployeeIds.includes(String(n.card.assignee_id)) ||
                n.filhas.some(f => filterEmployeeIds.includes(String(f.assignee_id)))
            );
        }
        if (busca.trim()) {
            const q = busca.trim().toLowerCase();
            const hit = (s) => String(s || '').toLowerCase().includes(q);
            lista = lista.filter(n =>
                hit(n.card.titulo) || hit(n.card.empresa) || hit(n.card.responsavel) ||
                n.filhas.some(f => hit(f.nome) || hit(f.tipo) || hit(f.responsavel))
            );
        }
        return lista;
    }, [arvore, filtro, busca, filterEmployeeIds]);

    const soltasVisiveis = useMemo(() => {
        let lista = filtro === 'todas' ? arvore.soltas : arvore.soltas.filter(s => s.status === filtro);
        if (filterEmployeeIds.length > 0) {
            lista = lista.filter(s => filterEmployeeIds.includes(String(s.a.assignee_id)));
        }
        if (busca.trim()) {
            const q = busca.trim().toLowerCase();
            const hit = (s) => String(s || '').toLowerCase().includes(q);
            lista = lista.filter(s => hit(s.a.nome) || hit(s.a.tipo) || hit(s.a.responsavel) || hit(s.a.empresa));
        }
        return lista;
    }, [arvore, filtro, busca, filterEmployeeIds]);

    const notasDe = useCallback((refType, refId) =>
        anotacoes.filter(n => n.ref_type === refType && String(n.ref_id) === String(refId)),
        [anotacoes]);

    // Todas as atividades vinculadas a um negócio (para a linha do tempo)
    const atividadesDoCard = useCallback((cardId) =>
        (atividades || []).filter(a => a.cardId != null && String(a.cardId) === String(cardId)),
        [atividades]);

    const toggleNotas = (key) => setAbertas(p => ({ ...p, [key]: !p[key] }));

    // ── Ficha de acompanhamento (modal): o clique na linha abre uma tela com tudo
    // (dados do item + atividades vinculadas + linha do tempo), em vez de expandir
    // a linha para baixo. Resolvida a partir dos dados atuais para refletir
    // anotações/remarcações feitas com a ficha aberta. ──
    const detalheNode = useMemo(() => {
        if (!detalhe) return null;
        if (detalhe.kind === 'card') {
            const node = arvore.nodes.find(n => String(n.card.id) === String(detalhe.id));
            return node ? { kind: 'card', node } : null;
        }
        const a = (atividades || []).find(x => String(x.id) === String(detalhe.id));
        return a ? { kind: 'atividade', a } : null;
    }, [detalhe, arvore, atividades]);

    useEffect(() => { if (detalhe && !detalheNode) setDetalhe(null); }, [detalhe, detalheNode]);

    // ── Remarcação ──────────────────────────────────────────────────────────
    const abrirRemarcar = (tipo, item) => {
        const itemHistorico = tipo === 'atividade' ? !atividadePendente(item) : !cardAtivo(item);
        if (itemHistorico) return;
        setRemarcando({ tipo, item });
        if (tipo === 'atividade') {
            setFormRemarcar({
                dataInicio: hoje, horaInicio: item.horaInicio || '09:00',
                dataFim: hoje, horaFim: item.horaFim || item.horaInicio || '10:00',
                dataFechamento: '',
            });
        } else {
            setFormRemarcar({ dataInicio: '', horaInicio: '', dataFim: '', horaFim: '', dataFechamento: hoje });
        }
    };

    const salvarRemarcacao = async () => {
        if (!remarcando) return;
        const { tipo, item } = remarcando;
        setSalvandoRemarcar(true);
        try {
            let notaAuto = '';
            if (tipo === 'atividade') {
                if (!formRemarcar.dataInicio) return;
                await updateAtividade(item.id, {
                    ...item,
                    dataInicio: formRemarcar.dataInicio,
                    horaInicio: formRemarcar.horaInicio,
                    dataFim: formRemarcar.dataFim || formRemarcar.dataInicio,
                    horaFim: formRemarcar.horaFim,
                });
                notaAuto = `Atividade remarcada de ${fmtData(item.dataFim || item.dataInicio)} para ${fmtData(formRemarcar.dataInicio)} às ${formRemarcar.horaInicio}.`;
            } else {
                if (!formRemarcar.dataFechamento) return;
                await updatePipelineCard(item.id, { dataFechamento: formRemarcar.dataFechamento });
                notaAuto = `Negócio remarcado: fechamento de ${fmtData(item.dataFechamento)} para ${fmtData(formRemarcar.dataFechamento)}.`;
            }
            // A remarcação entra na linha do tempo pela AUDITORIA do backend
            // (diff do update grava kind 'remarcacao') — sem POST manual aqui,
            // senão a entrada sairia duplicada. notaAuto vira só o texto do toast.
            await fetchAnotacoes();
            setRemarcando(null);
            toast({ title: 'Remarcado com sucesso', description: notaAuto, status: 'success', duration: 5000, isClosable: true, position: 'top-right' });
        } catch (err) {
            toast({ title: 'Erro ao remarcar', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
        } finally {
            setSalvandoRemarcar(false);
        }
    };

    const renderAtividadeRow = (a) => {
        const key = `atividade-${a.id}`;
        const status = statusDaAtividade(a);
        const historico = status === 'historico';
        return (
            <AtividadeRow
                key={a.id}
                a={a}
                status={status}
                historico={historico}
                notas={notasDe('atividade', a.id)}
                notasAbertas={!!abertas[key]}
                onToggleNotas={() => toggleNotas(key)}
                onRemarcar={historico ? undefined : () => abrirRemarcar('atividade', a)}
                onVer={() => navigate(`/comercial/atividades?openActivityId=${a.id}&mode=view`)}
                onChanged={fetchAnotacoes}
            />
        );
    };

    const totalListado = nodesFiltrados.length + soltasVisiveis.length;

    // ── Paginação: lista única (negócios + atividades avulsas juntos), TablePagination
    // compartilhado — 5 por padrão, opções 15/30/50 ──
    const [pagina, setPagina] = useState(1);
    const [itensPorPagina, setItensPorPagina] = useState(5);

    const itensListados = useMemo(() => ([
        ...nodesFiltrados.map(node => ({ kind: 'card', node })),
        ...soltasVisiveis.map(({ a }) => ({ kind: 'atividade', a })),
    ]), [nodesFiltrados, soltasVisiveis]);

    const totalPaginas = Math.max(1, Math.ceil(itensListados.length / itensPorPagina));
    const itensPaginados = useMemo(() =>
        itensListados.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina),
        [itensListados, pagina, itensPorPagina]);

    useEffect(() => { setPagina(1); }, [filtro, busca, filterEmployeeIds]);
    useEffect(() => { if (pagina > totalPaginas) setPagina(totalPaginas); }, [pagina, totalPaginas]);

    const PILLS = [
        { id: 'todas',    label: 'Todas',           count: contagens.todas },
        { id: 'atrasado', label: 'Atrasadas',       count: contagens.atrasado },
        { id: 'proximo',  label: 'Perto de vencer', count: contagens.proximo },
        { id: 'emDia',    label: 'Em dia',          count: contagens.emDia },
        { id: 'historico', label: 'Hist\u00f3rico', count: contagens.historico },
    ];

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header (mesmo padrão da tela Atividades) */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="100%">
                <Box flexShrink={0}>
                    <Heading size="lg" color="textPrimary" fontWeight="900">Anotações</Heading>
                </Box>
                <Flex gap={3} wrap="wrap" justify="flex-end" flex="1" minW="0" align="center">
                    <InputGroup maxW={{ base: '100%', md: '280px' }} minW="160px" flex="1 1 200px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="textSecondary" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar negócio, atividade..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                            color="textPrimary"
                            borderRadius="xl"
                        />
                    </InputGroup>
                    <Popover placement="bottom-end" closeOnBlur={true}>
                        <PopoverTrigger>
                            <Button
                                size="sm"
                                variant="outline"
                                borderColor="borderPrimary"
                                color="textPrimary"
                                leftIcon={<Icon as={AiOutlineFilter} boxSize={4} />}
                                _hover={{ bg: 'surfaceHover' }}
                                borderRadius="xl"
                                fontWeight="800"
                                flexShrink={0}
                            >
                                Filtros {filterEmployeeIds.length > 0 && `(${filterEmployeeIds.length})`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="surface" borderColor="borderPrimary" color="textPrimary" w="320px" borderRadius="xl" _focus={{ boxShadow: 'none' }} zIndex={999}>
                            <PopoverArrow bg="surface" />
                            <PopoverHeader borderColor="borderPrimary" fontWeight="bold" fontSize="sm">
                                Filtrar Responsáveis
                            </PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody maxH="400px" overflowY="auto" p={0}>
                                <Box p={3} borderBottom="1px solid" borderColor="borderPrimary">
                                    <Input
                                        placeholder="Buscar..."
                                        size="sm"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        value={filterSearch}
                                        onChange={(e) => setFilterSearch(e.target.value)}
                                        mb={2}
                                        borderRadius="md"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="green"
                                        w="100%"
                                        onClick={() => setFilterEmployeeIds([])}
                                        isDisabled={filterEmployeeIds.length === 0}
                                    >
                                        Limpar Filtros
                                    </Button>
                                </Box>
                                <Stack spacing={0}>
                                    {employees
                                        ?.filter(emp => {
                                            if (!filterSearch) return true;
                                            return emp.name?.toLowerCase().includes(filterSearch.toLowerCase());
                                        })
                                        .map((emp) => {
                                            const isChecked = filterEmployeeIds.includes(String(emp.id));
                                            return (
                                                <Box
                                                    key={emp.id}
                                                    px={4}
                                                    py={2}
                                                    cursor="pointer"
                                                    _hover={{ bg: "surfaceHover" }}
                                                    onClick={() => {
                                                        setFilterEmployeeIds(prev => {
                                                            const sid = String(emp.id);
                                                            if (prev.includes(sid)) return prev.filter(x => x !== sid);
                                                            return [...prev, sid];
                                                        });
                                                    }}
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={3}
                                                >
                                                    <Checkbox
                                                        isChecked={isChecked}
                                                        colorScheme="green"
                                                        borderColor="borderPrimary"
                                                        pointerEvents="none"
                                                    />
                                                    <Avatar size="xs" name={emp.name} src={emp.avatar || emp.url} />
                                                    <Text fontSize="sm" noOfLines={1}>{emp.name}</Text>
                                                </Box>
                                            );
                                        })}
                                    {(!employees || employees.length === 0) && (
                                        <Box p={4} textAlign="center" color="textMuted" fontSize="sm">
                                            Nenhum responsável encontrado.
                                        </Box>
                                    )}
                                </Stack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                </Flex>
            </Flex>

            {/* Filtros por estado de prazo */}
            <Flex gap={2} wrap="wrap">
                {PILLS.map(f => {
                    const active = filtro === f.id;
                    return (
                        <Button key={f.id} size="sm" variant="ghost"
                            border="1px solid" borderColor={active ? 'rgba(1,233,117,0.45)' : 'borderSubtle'} bg={active ? 'secondarySubtle' : 'transparent'} color={active ? 'secondary' : 'textSecondary'}
                            _hover={{ bg: active ? 'secondarySubtle' : 'surfaceHover', color: active ? 'secondary' : 'textPrimary' }}
                            borderRadius="lg" fontWeight="700" onClick={() => setFiltro(f.id)}
                            rightIcon={<Badge bg={active ? 'secondary' : 'surfaceRaised'} color={active ? 'primary' : 'textSecondary'} fontSize="xs" fontWeight="800" borderRadius="full" minW="20px" textAlign="center">{f.count}</Badge>}>
                            {f.label}
                        </Button>
                    );
                })}
            </Flex>

            {/* Estado vazio geral */}
            {contagens.todas === 0 && (
                <Flex direction="column" align="center" justify="center" bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="2xl" py={14} gap={3}>
                    <Flex w="64px" h="64px" borderRadius="2xl" bg="secondarySubtle" align="center" justify="center">
                        <Icon as={AiOutlineCheckCircle} color="secondary" boxSize="30px" />
                    </Flex>
                    <Heading size="md" color="textPrimary" fontWeight="900">Nada registrado ainda</Heading>
                    <Text fontSize="sm" color="textSecondary" maxW="420px" textAlign="center">
                        Negócios, atividades e seus históricos aparecem aqui para centralizar observações, acontecimentos e próximos acompanhamentos.
                    </Text>
                </Flex>
            )}

            {/* Vazio do filtro atual */}
            {contagens.todas > 0 && totalListado === 0 && (
                <Box bg="surface" border="1px dashed" borderColor="borderPrimary" borderRadius="xl" p={10} textAlign="center">
                    <Icon as={AiOutlineFileText} color="textMuted" boxSize="32px" mb={2} />
                    <Text color="textMuted" fontSize="sm">
                        {(busca.trim() || filterEmployeeIds.length > 0)
                            ? 'Nenhum item encontrado para a busca/filtro atual.'
                            : 'Nenhum item neste filtro.'}
                    </Text>
                </Box>
            )}

            {/* Lista única: negócios (árvore) + atividades avulsas juntos, paginados */}
            {totalListado > 0 && (
                <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                    <Flex
                        align="center"
                        gap={3}
                        p={4}
                        bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50'}
                        borderBottom="1px solid"
                        borderColor="borderPrimary"
                    >
                        <Icon as={AiOutlineFileText} color="secondary" boxSize="20px" />
                        <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1px" fontWeight="800">
                            Acompanhamento e histórico
                        </Heading>
                        <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">
                            {totalListado}
                        </Badge>
                    </Flex>

                    {/* Tabela com colunas alinhadas (mesmo padrão da tabela Atividades Vinculadas do Pipeline) */}
                    <Box overflowX="auto" sx={{
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-thumb': { bg: colorMode === 'dark' ? 'gray.800' : 'gray.300', borderRadius: 'full' },
                    }}>
                        <Box minW="1080px">
                            {/* Cabeçalho das colunas (ordem documentada em docs/telas/comercial-anotacoes.md) */}
                            <Grid
                                templateColumns={GRID_COLUMNS}
                                gap={3}
                                px={4}
                                py={3}
                                bg={colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.50'}
                                borderBottom="1px solid"
                                borderColor="borderPrimary"
                                fontSize="10px"
                                color="textSecondary"
                                fontWeight="900"
                                textTransform="uppercase"
                                letterSpacing="1.5px"
                                alignItems="center"
                            >
                                <Box minW={0}>Situação</Box>
                                <Box minW={0}>Item</Box>
                                <Box minW={0}>Tipo</Box>
                                <Box minW={0}>Responsável</Box>
                                <Box minW={0}>Prazo</Box>
                                <Box minW={0}>Valor</Box>
                                <Box minW={0}>Atividades</Box>
                                <Box minW={0}>Anotações</Box>
                                <Box minW={0} textAlign="right">Ações</Box>
                            </Grid>

                            {itensPaginados.map((item, idx) => {
                                const ultimo = idx === itensPaginados.length - 1;

                                // Atividade avulsa (sem card no Pipeline): mesma grade de colunas do negócio
                                if (item.kind === 'atividade') {
                                    const a = item.a;
                                    const st = statusDaAtividade(a);
                                    const historicoA = st === 'historico';
                                    const notasA = notasDe('atividade', a.id);
                                    const totalObsA = notasA.length + (temObservacaoLegada(a) ? 1 : 0);
                                    const situacaoA = situacaoDeAtividade(a);
                                    return (
                                        <Box key={`atividade-${a.id}`} borderBottom={ultimo ? 'none' : '1px solid'} borderColor="borderPrimary">
                                            {/* Clique na linha abre a ficha de acompanhamento (modal) */}
                                            <Grid
                                                templateColumns={GRID_COLUMNS}
                                                gap={3}
                                                px={4}
                                                py={3}
                                                alignItems="center"
                                                cursor="pointer"
                                                _hover={{ bg: 'surfaceHover' }}
                                                transition="background 0.15s"
                                                onClick={() => setDetalhe({ kind: 'atividade', id: a.id })}
                                            >
                                                <Box minW={0}>
                                                    <SituacaoBadge status={st} label={situacaoA.label} bg={situacaoA.bg} color={situacaoA.cor} />
                                                </Box>
                                                <Box minW={0}>
                                                    <Text fontSize="sm" fontWeight="700" color="textPrimary" noOfLines={1}>{a.nome}</Text>
                                                    {a.empresa && (
                                                        <HStack spacing={1}>
                                                            <Icon as={AiOutlineShop} boxSize="10px" color="textMuted" flexShrink={0} />
                                                            <Text fontSize="10px" color="textMuted" noOfLines={1}>{a.empresa}</Text>
                                                        </HStack>
                                                    )}
                                                </Box>
                                                <Box minW={0}>
                                                    <Text fontSize="xs" color="textSecondary" noOfLines={1}>{a.tipo || 'Atividade'}</Text>
                                                </Box>
                                                <Box minW={0}>
                                                    {a.responsavel ? (
                                                        <HStack spacing={1}>
                                                            <Icon as={AiOutlineUser} boxSize="11px" color="textMuted" flexShrink={0} />
                                                            <Text fontSize="xs" color="textSecondary" noOfLines={1}>{a.responsavel}</Text>
                                                        </HStack>
                                                    ) : <Text fontSize="xs" color="textMuted">—</Text>}
                                                </Box>
                                                <Box minW={0}>
                                                    <Text fontSize="xs" color="textSecondary">{fmtData(a.dataFim || a.dataInicio)}</Text>
                                                    <Text fontSize="10px" color="textMuted">{a.horaFim || a.horaInicio}</Text>
                                                </Box>
                                                <Box minW={0}><Text fontSize="xs" color="textMuted">—</Text></Box>
                                                <Box minW={0}><Text fontSize="xs" color="textMuted">—</Text></Box>
                                                <Box minW={0} onClick={e => e.stopPropagation()}>
                                                    <Tooltip label={`Abrir acompanhamento · ${totalObsA} registro(s)`} fontSize="xs">
                                                        <Button size="xs" variant="ghost" color={totalObsA > 0 ? 'secondary' : 'textSecondary'}
                                                            leftIcon={<AiOutlineMessage />} onClick={() => setDetalhe({ kind: 'atividade', id: a.id })} fontWeight="700">
                                                            {totalObsA}
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                                <HStack justify="flex-end" spacing={1} onClick={e => e.stopPropagation()}>
                                                    {!historicoA && (
                                                        <Tooltip label="Remarcar atividade" fontSize="xs">
                                                            <IconButton aria-label="Remarcar" icon={<AiOutlineCalendar />} size="xs" variant="ghost"
                                                                color="textSecondary" _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                                                onClick={() => abrirRemarcar('atividade', a)} />
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip label="Ver card — ficha completa em Atividades" fontSize="xs">
                                                        <IconButton aria-label="Ver card" icon={<AiOutlineEye />} size="xs" variant="ghost"
                                                            color="textSecondary" _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                                            onClick={() => navigate(`/comercial/atividades?openActivityId=${a.id}&mode=view`)} />
                                                    </Tooltip>
                                                </HStack>
                                            </Grid>
                                        </Box>
                                    );
                                }

                                const { card, statusProprio, filhas, historico } = item.node;
                                const key = `card-${card.id}`;
                                const notas = notasDe('card', card.id);
                                const totalObservacoes = notas.length + (temObservacaoLegada(card) ? 1 : 0);
                                const filhasAtrasadas = filhas.filter(f => statusDaAtividade(f) === 'atrasado');
                                const filhasProximas = filhas.filter(f => statusDaAtividade(f) === 'proximo');
                                const situacao = situacaoDeCard(statusProprio, historico, card.dataFechamento);
                                const filhasBadge = filhasAtrasadas.length > 0
                                    ? { bg: 'rgba(252,129,129,0.12)', cor: '#FC8181' }
                                    : filhasProximas.length > 0
                                        ? { bg: 'rgba(236,201,75,0.14)', cor: '#B7791F' }
                                        : { bg: 'surfaceRaised', cor: 'textSecondary' };
                                return (
                                    <Box key={key} borderBottom={ultimo ? 'none' : '1px solid'} borderColor="borderPrimary">
                                        {/* Linha do negócio — clique abre a ficha de acompanhamento (modal) */}
                                        <Grid
                                            templateColumns={GRID_COLUMNS}
                                            gap={3}
                                            px={4}
                                            py={3}
                                            alignItems="center"
                                            cursor="pointer"
                                            _hover={{ bg: 'surfaceHover' }}
                                            transition="background 0.15s"
                                            onClick={() => setDetalhe({ kind: 'card', id: card.id })}
                                        >
                                            <Box minW={0}>
                                                <SituacaoBadge status={historico ? 'historico' : statusProprio} label={situacao.label} bg={situacao.bg} color={situacao.cor} />
                                            </Box>
                                            <Box minW={0}>
                                                <Text fontSize="sm" fontWeight="800" color="textPrimary" noOfLines={1}>{card.titulo}</Text>
                                                {card.empresa && (
                                                    <HStack spacing={1}>
                                                        <Icon as={AiOutlineShop} boxSize="10px" color="textMuted" flexShrink={0} />
                                                        <Text fontSize="10px" color="textMuted" noOfLines={1}>{card.empresa}</Text>
                                                    </HStack>
                                                )}
                                            </Box>
                                            <Box minW={0}>
                                                <Text fontSize="xs" color="textSecondary">Negócio</Text>
                                            </Box>
                                            <Box minW={0}>
                                                {card.responsavel ? (
                                                    <HStack spacing={1}>
                                                        <Icon as={AiOutlineUser} boxSize="11px" color="textMuted" flexShrink={0} />
                                                        <Text fontSize="xs" color="textSecondary" noOfLines={1}>{card.responsavel}</Text>
                                                    </HStack>
                                                ) : <Text fontSize="xs" color="textMuted">—</Text>}
                                            </Box>
                                            <Box minW={0}>
                                                <Text fontSize="xs" color="textSecondary">{fmtData(card.dataFechamento)}</Text>
                                            </Box>
                                            <Box minW={0}>
                                                <Text fontSize="xs" fontWeight="700" color="textPrimary">{fmtMoney(card.valor)}</Text>
                                            </Box>
                                            <Box minW={0}>
                                                {filhas.length > 0 ? (
                                                    <Tooltip label={`${filhasAtrasadas.length} atrasada(s) · ${filhasProximas.length} perto de vencer`} fontSize="xs">
                                                        <Badge bg={filhasBadge.bg} color={filhasBadge.cor} fontSize="9px" fontWeight="900" borderRadius="md" px={2}>
                                                            {filhas.length}
                                                        </Badge>
                                                    </Tooltip>
                                                ) : <Text fontSize="xs" color="textMuted">—</Text>}
                                            </Box>
                                            <Box minW={0} onClick={e => e.stopPropagation()}>
                                                <Tooltip label={`Abrir acompanhamento · ${totalObservacoes} registro(s)`} fontSize="xs">
                                                    <Button size="xs" variant="ghost" color={totalObservacoes > 0 ? 'secondary' : 'textSecondary'}
                                                        leftIcon={<AiOutlineMessage />} onClick={() => setDetalhe({ kind: 'card', id: card.id })} fontWeight="700">
                                                        {totalObservacoes}
                                                    </Button>
                                                </Tooltip>
                                            </Box>
                                            <HStack justify="flex-end" spacing={1} onClick={e => e.stopPropagation()}>
                                                {!historico && (
                                                    <Tooltip label="Remarcar fechamento do negócio" fontSize="xs">
                                                        <IconButton aria-label="Remarcar negócio" icon={<AiOutlineCalendar />} size="xs" variant="ghost"
                                                            color="textSecondary" _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                                            onClick={() => abrirRemarcar('card', card)} />
                                                    </Tooltip>
                                                )}
                                                <Tooltip label="Ver card — ficha completa em Negócios" fontSize="xs">
                                                    <IconButton aria-label="Ver card" icon={<AiOutlineEye />} size="xs" variant="ghost"
                                                        color="textSecondary" _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                                        onClick={() => navigate(`/comercial/negocios?openCardId=${card.id}`)} />
                                                </Tooltip>
                                            </HStack>
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <TablePagination
                        page={pagina}
                        pageSize={itensPorPagina}
                        totalItems={itensListados.length}
                        onPageChange={setPagina}
                        onPageSizeChange={(n) => { setItensPorPagina(n); setPagina(1); }}
                    />
                </Box>
            )}

            {/* Modal: Ficha de acompanhamento — o clique na linha abre uma tela com tudo:
                dados do item + atividades vinculadas + linha do tempo */}
            <Modal isOpen={!!detalheNode} onClose={() => setDetalhe(null)} isCentered size="4xl" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent>
                    {detalheNode && (() => {
                        const ehCard = detalheNode.kind === 'card';
                        const card = ehCard ? detalheNode.node.card : null;
                        const a = ehCard ? null : detalheNode.a;
                        const filhas = ehCard ? detalheNode.node.filhas : [];
                        const historicoItem = ehCard ? detalheNode.node.historico : statusDaAtividade(a) === 'historico';
                        const situ = ehCard
                            ? situacaoDeCard(detalheNode.node.statusProprio, detalheNode.node.historico, card.dataFechamento)
                            : situacaoDeAtividade(a);
                        const stC = ehCard
                            ? STATUS_CORES[detalheNode.node.historico ? 'historico' : detalheNode.node.status]
                            : STATUS_CORES[statusDaAtividade(a)];
                        const empresa = ehCard ? card.empresa : a.empresa;
                        return (
                            <>
                                <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pb={4} pr={12}>
                                    <Flex align="center" gap={3} wrap="wrap">
                                        <Flex w="40px" h="40px" borderRadius="lg" bg={stC.bg} align="center" justify="center" flexShrink={0}>
                                            <Icon as={ehCard ? AiOutlineDollarCircle : (TIPO_ICONES[a?.tipo] || AiOutlineSchedule)} color={stC.cor} boxSize="20px" />
                                        </Flex>
                                        <Box minW={0} flex="1">
                                            <Text fontSize="10px" fontWeight="800" color="textMuted" textTransform="uppercase" letterSpacing="1.5px">
                                                {ehCard ? 'Acompanhamento do negócio' : 'Acompanhamento da atividade'}
                                            </Text>
                                            <Text fontSize="lg" fontWeight="800" color="textPrimary" noOfLines={1}>
                                                {ehCard ? card.titulo : a.nome}
                                            </Text>
                                            {empresa && (
                                                <HStack spacing={1}>
                                                    <Icon as={AiOutlineShop} boxSize="11px" color="textMuted" />
                                                    <Text fontSize="xs" color="textMuted" noOfLines={1}>{empresa}</Text>
                                                </HStack>
                                            )}
                                        </Box>
                                        <SituacaoBadge status={ehCard ? (detalheNode.node.historico ? 'historico' : detalheNode.node.statusProprio) : statusDaAtividade(a)} label={situ.label} bg={situ.bg} color={situ.cor} />
                                    </Flex>
                                </ModalHeader>
                                <ModalCloseButton color="textSecondary" top={4} />
                                <ModalBody py={5}>
                                    {/* Dados principais do item */}
                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                                        <CampoInfo label="Tipo">
                                            <Text fontSize="sm" fontWeight="700" color="textPrimary">{ehCard ? 'Negócio' : (a.tipo || 'Atividade')}</Text>
                                        </CampoInfo>
                                        <CampoInfo label="Responsável">
                                            <Text fontSize="sm" fontWeight="700" color="textPrimary" noOfLines={1}>{(ehCard ? card.responsavel : a.responsavel) || '—'}</Text>
                                        </CampoInfo>
                                        <CampoInfo label={ehCard ? 'Fechamento' : 'Prazo'}>
                                            <Text fontSize="sm" fontWeight="700" color="textPrimary">
                                                {fmtData(ehCard ? card.dataFechamento : (a.dataFim || a.dataInicio))}
                                                {!ehCard && (a.horaFim || a.horaInicio) ? ` · ${a.horaFim || a.horaInicio}` : ''}
                                            </Text>
                                        </CampoInfo>
                                        {ehCard ? (
                                            <CampoInfo label="Valor">
                                                <Text fontSize="sm" fontWeight="800" color="textPrimary">{fmtMoney(card.valor)}</Text>
                                            </CampoInfo>
                                        ) : (
                                            <CampoInfo label="Anotações">
                                                <Text fontSize="sm" fontWeight="700" color="textPrimary">
                                                    {notasDe('atividade', a.id).length + (temObservacaoLegada(a) ? 1 : 0)} registro(s)
                                                </Text>
                                            </CampoInfo>
                                        )}
                                    </SimpleGrid>

                                    {ehCard ? (
                                        <>
                                            {/* Seção 1 — Atividades vinculadas: caixa própria (accent azul),
                                                separada da linha do tempo do negócio para a hierarquia ficar clara */}
                                            {filhas.length > 0 && (
                                                <Box bg="surfaceRaised" border="1px solid" borderColor="borderSubtle"
                                                    borderLeft="3px solid" borderLeftColor="#4299E1" borderRadius="lg" overflow="hidden">
                                                    <Flex align="center" gap={2} px={3} py={2.5}
                                                        bg="rgba(66,153,225,0.08)" borderBottom="1px solid" borderColor="borderSubtle">
                                                        <Flex w="26px" h="26px" borderRadius="md" bg="rgba(66,153,225,0.15)" align="center" justify="center" flexShrink={0}>
                                                            <Icon as={AiOutlineSchedule} color="#4299E1" boxSize="14px" />
                                                        </Flex>
                                                        <Box minW={0}>
                                                            <Text fontSize="xs" fontWeight="800" color="textPrimary" textTransform="uppercase" letterSpacing="1px">
                                                                Atividades vinculadas
                                                            </Text>
                                                            <Text fontSize="10px" color="textMuted" noOfLines={1}>
                                                                Tarefas e eventos ligados a este negócio — clique em uma atividade para ver a linha do tempo dela
                                                            </Text>
                                                        </Box>
                                                        <Badge ml="auto" bg="rgba(66,153,225,0.15)" color="#4299E1" fontSize="10px" fontWeight="800" borderRadius="full" px={2}>
                                                            {filhas.length}
                                                        </Badge>
                                                    </Flex>
                                                    <VStack align="stretch" spacing={2} p={3}>
                                                        {filhas.map(af => renderAtividadeRow(af))}
                                                    </VStack>
                                                </Box>
                                            )}

                                            {/* Seção 2 — Linha do tempo do negócio (accent verde) */}
                                            <TimelineSection
                                                refType="card"
                                                refId={card.id}
                                                titulo="Linha do tempo do negócio"
                                                subtitulo={`Observações, remarcações e acontecimentos de "${card.titulo}"`}
                                                notas={notasDe('card', card.id)}
                                                eventos={eventosDeCard(card, detalheNode.node.statusProprio === 'atrasado', atividadesDoCard(card.id))}
                                                onChanged={fetchAnotacoes}
                                            />
                                        </>
                                    ) : (
                                        <TimelineSection
                                            refType="atividade"
                                            refId={a.id}
                                            titulo="Linha do tempo da atividade"
                                            subtitulo={a.nome}
                                            accent="atividade"
                                            notas={notasDe('atividade', a.id)}
                                            eventos={eventosDeAtividade(a, false)}
                                            onChanged={fetchAnotacoes}
                                        />
                                    )}
                                </ModalBody>
                                <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3} flexWrap="wrap" rowGap={2}>
                                    {!historicoItem && (
                                        <Button size="sm" variant="outline" borderColor="borderPrimary" color="textPrimary" flexShrink={0}
                                            leftIcon={<AiOutlineCalendar />} _hover={{ bg: 'surfaceHover' }}
                                            onClick={() => abrirRemarcar(ehCard ? 'card' : 'atividade', ehCard ? card : a)}>
                                            Remarcar
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" borderColor="borderPrimary" color="textPrimary" flexShrink={0}
                                        leftIcon={<AiOutlineEye />} _hover={{ bg: 'surfaceHover' }}
                                        onClick={() => navigate(ehCard ? `/comercial/negocios?openCardId=${card.id}` : `/comercial/atividades?openActivityId=${a.id}&mode=view`)}>
                                        Ver card
                                    </Button>
                                    <Button size="sm" variant="primary" ml="auto" flexShrink={0} onClick={() => setDetalhe(null)}>
                                        Fechar
                                    </Button>
                                </ModalFooter>
                            </>
                        );
                    })()}
                </ModalContent>
            </Modal>

            {/* Modal: Remarcar */}
            <Modal isOpen={!!remarcando} onClose={() => setRemarcando(null)} isCentered size="md">
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent>
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pb={4}>
                        <HStack spacing={2}>
                            <Icon as={AiOutlineCalendar} color="secondary" />
                            <Text fontSize="md" fontWeight="800">
                                {remarcando?.tipo === 'atividade' ? 'Remarcar Atividade' : 'Remarcar Negócio'}
                            </Text>
                        </HStack>
                        <Text fontSize="xs" color="textMuted" fontWeight="500" mt={1} noOfLines={1}>
                            {remarcando?.item?.nome || remarcando?.item?.titulo}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton color="textSecondary" top={4} />
                    <ModalBody py={5}>
                        {remarcando?.tipo === 'atividade' ? (
                            <SimpleGrid columns={2} spacing={3}>
                                <FormControl isRequired>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Nova data início</FormLabel>
                                    <Input type="date" min={hoje} value={formRemarcar.dataInicio}
                                        onChange={e => setFormRemarcar(p => ({ ...p, dataInicio: e.target.value, dataFim: e.target.value > (p.dataFim || '') ? e.target.value : p.dataFim }))} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Hora início</FormLabel>
                                    <Input type="time" value={formRemarcar.horaInicio}
                                        onChange={e => setFormRemarcar(p => ({ ...p, horaInicio: e.target.value }))} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Nova data fim</FormLabel>
                                    <Input type="date" min={formRemarcar.dataInicio || hoje} value={formRemarcar.dataFim}
                                        onChange={e => setFormRemarcar(p => ({ ...p, dataFim: e.target.value }))} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Hora fim</FormLabel>
                                    <Input type="time" value={formRemarcar.horaFim}
                                        onChange={e => setFormRemarcar(p => ({ ...p, horaFim: e.target.value }))} />
                                </FormControl>
                            </SimpleGrid>
                        ) : (
                            <FormControl isRequired>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Nova data de fechamento</FormLabel>
                                <Input type="date" min={hoje} value={formRemarcar.dataFechamento}
                                    onChange={e => setFormRemarcar(p => ({ ...p, dataFechamento: e.target.value }))} />
                            </FormControl>
                        )}
                        <Text fontSize="11px" color="textMuted" mt={4}>
                            A remarcação fica registrada automaticamente como anotação do item.
                        </Text>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" size="sm" color="textSecondary" onClick={() => setRemarcando(null)}>Cancelar</Button>
                        <Button variant="primary" size="sm" leftIcon={<AiOutlineCalendar />} onClick={salvarRemarcacao}
                            isLoading={salvandoRemarcar}
                            isDisabled={remarcando?.tipo === 'atividade' ? !formRemarcar.dataInicio : !formRemarcar.dataFechamento}>
                            Remarcar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};

export default Anotacoes;
